import { Link } from 'react-router-dom';
import { Beaker, Shield, BarChart3, ArrowUpRight, Target } from 'lucide-react';

export default function BlogPolymarketPaperTrading() {
  return (
    <div className="min-h-screen bg-dark-900 text-white py-8 px-4 sm:px-6 lg:px-8">
      <main className="max-w-4xl mx-auto space-y-10 animate-fade-in">
        <header className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-600/15 border border-primary-500/40 text-xs text-primary-100 uppercase tracking-wide">
            <Beaker className="w-3 h-3" />
            <span>Paper trading</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            Paper Trading on Polymarket: How to Stress-Test a Strategy Without Burning Real USDC.e
          </h1>
          <p className="text-dark-300 text-sm sm:text-base max-w-2xl">
            Paper trading is not a toy if you treat it like a lab. Done correctly, it lets you validate a
            Polymarket trading idea, sizing model, and risk rules across real markets and real price
            movement — without paying the tuition in actual losses. This guide explains how to structure a
            paper track so it predicts real performance.
          </p>
        </header>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary-400" />
            The #1 mistake: paper trading that can never lose
          </h2>
          <p className="text-sm text-dark-300">
            Most people "paper trade" in a way that inflates results:
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              They assume perfect fills at mid, even when the book is thin.
            </li>
            <li>
              They ignore slippage during news spikes.
            </li>
            <li>
              They overtrade because there is no emotional cost.
            </li>
            <li>
              They change rules midstream (the silent killer).
            </li>
          </ul>
          <p className="text-sm text-dark-300">
            A useful paper system needs constraints that mimic reality: entry rules, exit rules, max
            exposure, and a consistent record of every decision.
          </p>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary-400" />
            What to measure (and what to ignore)
          </h2>
          <p className="text-sm text-dark-300">
            If you only track win-rate, you can end up copying a strategy that wins often but blows up
            rarely. Track these instead:
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              <span className="font-medium">Max drawdown</span> – the single number that tells you if your
              sizing is too aggressive.
            </li>
            <li>
              <span className="font-medium">Exposure usage</span> – how often you hit your max exposure cap.
              If it&apos;s always maxed, you&apos;re not diversified.
            </li>
            <li>
              <span className="font-medium">Average hold time</span> – paper results can look great if you
              always hold forever and never realize losses.
            </li>
            <li>
              <span className="font-medium">Entry quality</span> – not "was I right", but "did I pay a bad
              price".
            </li>
            <li>
              <span className="font-medium">Loss clustering</span> – do losses arrive in bursts when the
              narrative changes? That&apos;s a portfolio risk problem.
            </li>
          </ul>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Target className="w-5 h-5 text-primary-400" />
            A practical paper workflow for Polymarket
          </h2>
          <ol className="text-sm text-dark-300 list-decimal list-inside space-y-2">
            <li>
              Pick one market category to start (elections, sports, macro, crypto). Don&apos;t paper trade
              everything.
            </li>
            <li>
              Choose a fixed notional per trade (example: $10–$50) and a hard max exposure (example: $200).
            </li>
            <li>
              Define exits in advance: a stop loss and a take profit, even if you plan to adjust later.
            </li>
            <li>
              Log every trade reason. If you can&apos;t explain why you entered, you can&apos;t improve the system.
            </li>
            <li>
              Review weekly, not hourly. The point is to test the rules, not your dopamine response.
            </li>
          </ol>
          <p className="text-xs text-dark-500">
            If you&apos;re using BetMimic, paper trading is most useful when it mirrors your real setup: the same
            copy configs, the same sizing, and the same risk constraints.
          </p>
        </section>

        <section className="space-y-3">
          <div className="flex flex-wrap gap-3 text-sm">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              Read more Polymarket guides
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600/90 hover:bg-primary-500 transition-colors"
            >
              <ArrowUpRight className="w-4 h-4" />
              View plans
            </Link>
            <a
              href="https://polymarket.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
            >
              <ArrowUpRight className="w-4 h-4" />
              Visit Polymarket.com
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}
